import React, { useState, useEffect, useReducer, useContext } from "react";
import { toast } from "react-toastify";
import { useHistory } from "react-router-dom";

import { makeStyles } from "@material-ui/core/styles";
import Paper from "@material-ui/core/Paper";
import Button from "@material-ui/core/Button";
import Table from "@material-ui/core/Table";
import TableBody from "@material-ui/core/TableBody";
import TableCell from "@material-ui/core/TableCell";
import TableHead from "@material-ui/core/TableHead";
import TableRow from "@material-ui/core/TableRow";
import IconButton from "@material-ui/core/IconButton";
import Tooltip from "@material-ui/core/Tooltip";
import SearchIcon from "@material-ui/icons/Search";
import TextField from "@material-ui/core/TextField";
import InputAdornment from "@material-ui/core/InputAdornment";

import AddIcon from "@material-ui/icons/Add";
import AnnouncementOutlinedIcon from "@material-ui/icons/AnnouncementOutlined";
import DeleteOutlineIcon from "@material-ui/icons/DeleteOutline";
import EditIcon from "@material-ui/icons/Edit";

import MainContainer from "../../components/MainContainer";
import { PageHeader, StatusBadge } from "../../components/ui";
import EmptyState from "../../components/EmptyState";
import { getTokens } from "../../styles/tokens";

import api from "../../services/api";
import { i18n } from "../../translate/i18n";
import TableRowSkeleton from "../../components/TableRowSkeleton";
import AnnouncementModal from "../../components/AnnouncementModal";
import ConfirmationModal from "../../components/ConfirmationModal";
import toastError from "../../errors/toastError";
import { isArray } from "lodash";
// import { SocketContext } from "../../context/Socket/SocketContext";


import { AuthContext } from "../../context/Auth/AuthContext";

const reducer = (state, action) => {
  if (action.type === "LOAD_ANNOUNCEMENTS") {
    const announcements = action.payload;
    const newAnnouncements = [];

    if (isArray(announcements)) {
      announcements.forEach((announcement) => {
        const announcementIndex = state.findIndex(
          (u) => u.id === announcement.id
        );
        if (announcementIndex !== -1) {
          state[announcementIndex] = announcement;
        } else {
          newAnnouncements.push(announcement);
        }
      });
    }

    return [...state, ...newAnnouncements];
  }

  if (action.type === "UPDATE_ANNOUNCEMENTS") {
    const announcement = action.payload;
    const announcementIndex = state.findIndex((u) => u.id === announcement.id);

    if (announcementIndex !== -1) {
      state[announcementIndex] = announcement;
      return [...state];
    } else {
      return [announcement, ...state];
    }
  }

  if (action.type === "DELETE_ANNOUNCEMENT") {
    const announcementId = action.payload;

    const announcementIndex = state.findIndex((u) => u.id === announcementId);
    if (announcementIndex !== -1) {
      state.splice(announcementIndex, 1);
    }
    return [...state];
  }

  if (action.type === "RESET") {
    return [];
  }
};

const useStyles = makeStyles((theme) => {
  const t = getTokens(theme.palette.type, theme.palette.primary.main);
  return {
    filters: {
      display: "flex",
      flexWrap: "wrap",
      alignItems: "center",
      gap: 12,
      marginBottom: 16,
    },
    search: {
      width: "min(320px, 100%)",
      [theme.breakpoints.down("xs")]: { width: "100%" },
    },
    searchIcon: { color: t.textMuted },
    mainPaper: {
      flex: 1,
      minHeight: 0,
      display: "flex",
      flexDirection: "column",
      overflow: "hidden",
      boxShadow: t.shadow.card,
    },
    tableScroll: {
      flex: 1,
      minHeight: 0,
      overflow: "auto",
      ...theme.scrollbarStyles,
    },
    table: { minWidth: 680 },
    titleCell: { fontWeight: 600, color: t.text },
    muted: { color: t.textMuted },
    actionsCell: { whiteSpace: "nowrap" },
    deleteButton: { color: t.semantic.error },
  };
});

const PRIORITY_TONE = { 1: "error", 2: "warning", 3: "neutral" };

const Announcements = () => {
  const classes = useStyles();
  const history = useHistory();
//   const socketManager = useContext(SocketContext);
  const { user, socket } = useContext(AuthContext);


  const [loading, setLoading] = useState(false);
  const [pageNumber, setPageNumber] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [selectedAnnouncement, setSelectedAnnouncement] = useState(null);
  const [deletingAnnouncement, setDeletingAnnouncement] = useState(null);
  const [announcementModalOpen, setAnnouncementModalOpen] = useState(false);
  const [confirmModalOpen, setConfirmModalOpen] = useState(false);
  const [searchParam, setSearchParam] = useState("");
  const [announcements, dispatch] = useReducer(reducer, []);

  // trava para nao acessar pagina que não pode  
  useEffect(() => {
    async function fetchData() {
      if (!user.super) {
        toast.error("Esta empresa não possui permissão para acessar essa página! Estamos lhe redirecionando.");
        setTimeout(() => {
          history.push(`/`)
        }, 1000);
      }
    }
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    dispatch({ type: "RESET" });
    setPageNumber(1);
  }, [searchParam]);

  useEffect(() => {
    setLoading(true);
    const delayDebounceFn = setTimeout(() => {
      fetchAnnouncements();
    }, 500);
    return () => clearTimeout(delayDebounceFn);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParam, pageNumber]);

  useEffect(() => {
    if (user.companyId && socket) {
//    const socket = socketManager.GetSocket();

      const onCompanyAnnouncement = (data) => {
        if (data.action === "update" || data.action === "create") {
          dispatch({ type: "UPDATE_ANNOUNCEMENTS", payload: data.record });
        }
        if (data.action === "delete") {
          dispatch({ type: "DELETE_ANNOUNCEMENT", payload: +data.id });
        }
      }

      socket.on(`company-announcement`, onCompanyAnnouncement);
      return () => {
        socket.off(`company-announcement`, onCompanyAnnouncement);
      }
    }
    return undefined;
  }, [user]);

  const fetchAnnouncements = async () => {
    try {
      const { data } = await api.get("/announcements/", {
        params: { searchParam, pageNumber },
      });
      dispatch({ type: "LOAD_ANNOUNCEMENTS", payload: data.records });
      setHasMore(data.hasMore);
      setLoading(false);
    } catch (err) {
      toastError(err);
    }
  };

  const handleOpenAnnouncementModal = () => {
    setSelectedAnnouncement(null);
    setAnnouncementModalOpen(true);
  };

  const handleCloseAnnouncementModal = () => {
    setSelectedAnnouncement(null);
    setAnnouncementModalOpen(false);
  };

  const handleSearch = (event) => {
    setSearchParam(event.target.value.toLowerCase());
  };

  const handleEditAnnouncement = (announcement) => {
    setSelectedAnnouncement(announcement);
    setAnnouncementModalOpen(true);
  };

  const handleDeleteAnnouncement = async (announcement) => {
    try {
      if (announcement.mediaName)
        await api.delete(`/announcements/${announcement.id}/media-upload`);

      await api.delete(`/announcements/${announcement.id}`);

      toast.success(i18n.t("announcements.toasts.deleted"));
    } catch (err) {
      toastError(err);
    }
    setDeletingAnnouncement(null);
    setSearchParam("");
    setPageNumber(1);
  };

  const loadMore = () => {
    setPageNumber((prevState) => prevState + 1);
  };

  const handleScroll = (e) => {
    if (!hasMore || loading) return;
    const { scrollTop, scrollHeight, clientHeight } = e.currentTarget;
    if (scrollHeight - (scrollTop + 100) < clientHeight) {
      loadMore();
    }
  };

  const translatePriority = (val) => {
    if (val === 1) {
      return "Alta";
    }
    if (val === 2) {
      return "Média";
    }
    if (val === 3) {
      return "Baixa";
    }
  };

  return (
    <MainContainer maxWidth>
      <ConfirmationModal
        title={
          deletingAnnouncement &&
          `${i18n.t("announcements.confirmationModal.deleteTitle")} ${deletingAnnouncement.title
          }?`
        }
        open={confirmModalOpen}
        onClose={setConfirmModalOpen}
        onConfirm={() => handleDeleteAnnouncement(deletingAnnouncement)}
      >
        {i18n.t("announcements.confirmationModal.deleteMessage")}
      </ConfirmationModal>
      <AnnouncementModal
        resetPagination={() => {
          setPageNumber(1);
          fetchAnnouncements();
        }}
        open={announcementModalOpen}
        onClose={handleCloseAnnouncementModal}
        aria-labelledby="form-dialog-title"
        announcementId={selectedAnnouncement && selectedAnnouncement.id}
      />
      <PageHeader
        title={i18n.t("announcements.title")}
        subtitle="Avisos da plataforma exibidos aos usuários."
        badge={
          <StatusBadge
            color="neutral"
            dot={false}
            label={String(announcements.length)}
          />
        }
        actions={
          <Button
            variant="contained"
            onClick={handleOpenAnnouncementModal}
            color="primary"
            startIcon={<AddIcon />}
          >
            {i18n.t("announcements.buttons.add")}
          </Button>
        }
      />
      <div className={classes.filters}>
        <TextField
          className={classes.search}
          variant="outlined"
          size="small"
          placeholder={i18n.t("announcements.searchPlaceholder")}
          type="search"
          value={searchParam}
          onChange={handleSearch}
          inputProps={{ "aria-label": i18n.t("announcements.searchPlaceholder") }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" className={classes.searchIcon} />
              </InputAdornment>
            ),
          }}
        />
      </div>
      <Paper className={classes.mainPaper} variant="outlined">
        <div className={classes.tableScroll} onScroll={handleScroll}>
          <Table size="small" className={classes.table}>
            <TableHead>
              <TableRow>
                <TableCell>{i18n.t("announcements.table.title")}</TableCell>
                <TableCell>{i18n.t("announcements.table.priority")}</TableCell>
                <TableCell>{i18n.t("announcements.table.mediaName")}</TableCell>
                <TableCell>{i18n.t("announcements.table.status")}</TableCell>
                <TableCell align="right">
                  {i18n.t("announcements.table.actions")}
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              <>
                {announcements.map((announcement) => (
                  <TableRow key={announcement.id}>
                    <TableCell className={classes.titleCell}>
                      {announcement.title}
                    </TableCell>
                    <TableCell>
                      {translatePriority(announcement.priority) && (
                        <StatusBadge
                          color={PRIORITY_TONE[announcement.priority] || "neutral"}
                          label={translatePriority(announcement.priority)}
                        />
                      )}
                    </TableCell>
                    <TableCell
                      className={announcement.mediaName ? undefined : classes.muted}
                    >
                      {announcement.mediaName ?? i18n.t("quickMessages.noAttachment")}
                    </TableCell>
                    <TableCell>
                      <StatusBadge
                        color={announcement.status ? "success" : "neutral"}
                        label={
                          announcement.status
                            ? i18n.t("announcements.active")
                            : i18n.t("announcements.inactive")
                        }
                      />
                    </TableCell>
                    <TableCell align="right" className={classes.actionsCell}>
                      <Tooltip title="Editar">
                        <IconButton
                          size="small"
                          aria-label="Editar informativo"
                          onClick={() => handleEditAnnouncement(announcement)}
                        >
                          <EditIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Excluir">
                        <IconButton
                          size="small"
                          aria-label="Excluir informativo"
                          className={classes.deleteButton}
                          onClick={(e) => {
                            setConfirmModalOpen(true);
                            setDeletingAnnouncement(announcement);
                          }}
                        >
                          <DeleteOutlineIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))}
                {!loading && announcements.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={5} style={{ borderBottom: "none" }}>
                      <EmptyState
                        icon={<AnnouncementOutlinedIcon />}
                        title={
                          searchParam
                            ? "Nenhum informativo encontrado"
                            : "Nenhum informativo cadastrado"
                        }
                        message={
                          searchParam
                            ? "Tente outro termo de busca."
                            : "Crie um informativo para avisar os usuários sobre novidades e manutenções."
                        }
                        action={
                          !searchParam && (
                            <Button
                              variant="contained"
                              color="primary"
                              startIcon={<AddIcon />}
                              onClick={handleOpenAnnouncementModal}
                            >
                              {i18n.t("announcements.buttons.add")}
                            </Button>
                          )
                        }
                      />
                    </TableCell>
                  </TableRow>
                )}
                {loading && <TableRowSkeleton columns={5} />}
              </>
            </TableBody>
          </Table>
        </div>
      </Paper>
    </MainContainer>
  )
};

export default Announcements;
